import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { wsUrl, emitGuard } from "@/lib/api";

export function useRoomSocket(roomId, enabled, { onKicked, onEnded, onBlocked }) {
  const wsRef = useRef(null);
  const handlers = useRef({ onKicked, onEnded, onBlocked });
  handlers.current = { onKicked, onEnded, onBlocked };
  const [state, setState] = useState({ connected: false, messages: [], stage: [], queue: [], viewers: 0, isModerator: false, stageStatus: "none", alerts: [] });
  const [reactions, setReactions] = useState([]);

  useEffect(() => {
    if (!enabled) return;
    const ws = new WebSocket(wsUrl(`/ws/rooms/${roomId}`));
    wsRef.current = ws;
    const patch = (p) => setState((s) => ({ ...s, ...(typeof p === "function" ? p(s) : p) }));
    ws.onopen = () => patch({ connected: true });
    ws.onclose = () => patch({ connected: false });
    ws.onmessage = (e) => {
      const d = JSON.parse(e.data);
      switch (d.type) {
        case "init":
          patch({ messages: d.messages, stage: d.stage, queue: d.queue, viewers: d.viewers, isModerator: d.is_moderator, stageStatus: d.stage_status });
          break;
        case "chat":
          patch((s) => ({ messages: [...s.messages.slice(-120), d.message] }));
          break;
        case "presence":
          patch({ viewers: d.viewers });
          break;
        case "stage":
          patch({ stage: d.stage });
          break;
        case "queue":
          patch({ queue: d.queue });
          break;
        case "stage_status":
          patch({ stageStatus: d.status });
          if (d.status === "on_stage") toast.success("Você entrou na roda! 🎙️");
          if (d.status === "rejected") toast("O moderador não aprovou sua entrada desta vez.");
          break;
        case "reaction": {
          const id = Math.random().toString(36).slice(2);
          setReactions((r) => [...r.slice(-25), { id, kind: d.kind, left: 10 + Math.random() * 70 }]);
          setTimeout(() => setReactions((r) => r.filter((x) => x.id !== id)), 2500);
          break;
        }
        case "mod_alert":
          patch((s) => ({ alerts: [d.alert, ...s.alerts].slice(0, 50) }));
          toast.warning(`Alerta: ${d.alert.reason}`, { description: d.alert.user_name || d.alert.reporter_name });
          break;
        case "moderated":
          toast.error(d.blocked ? "Perfil bloqueado preventivamente pelo Guarda-Costas IA" : "Mensagem bloqueada pelo Guarda-Costas IA", { description: d.reason });
          if (d.strikes != null || d.blocked) emitGuard({ field: "Chat da live", reason: d.reason, strikes: d.strikes, blocked: d.blocked });
          if (d.blocked) handlers.current.onBlocked?.();
          break;
        case "blocked":
          toast.error(d.reason);
          handlers.current.onBlocked?.();
          break;
        case "kicked":
          handlers.current.onKicked?.(d.reason);
          break;
        case "ended":
          handlers.current.onEnded?.();
          break;
        case "error":
          toast.error(d.message);
          break;
        default:
      }
    };
    return () => ws.close();
  }, [roomId, enabled]);

  const send = useCallback((type, payload = {}) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) ws.send(JSON.stringify({ type, ...payload }));
  }, []);

  return { ...state, reactions, send };
}
